import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ReactComponent as Instagram } from './icons/icons8-instagram.svg';
import ConvertKitForm from 'convertkit-react';
import {
  ANALYTICS_CONSENT_UPDATED_EVENT,
  clearAnalyticsConsent,
  getAnalyticsConsent,
  getAnalyticsConsentStatusLabel,
  openCookieSettings,
  trackEventIfConsented,
} from './analyticsConsent';
import { getPageGroupFromPath } from './pageAnalytics';
import { AMAZON_ASSOCIATE_LINE, AMAZON_GLOBAL_DISCLOSURE, AMAZON_STORE_DISCLOSURE } from './amazonAffiliate';
import './style.css';

const MY_FORM_ID = 5547918;

const external = url => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

const newsletterDisclosure =
  'By subscribing, you agree to receive 101Setups emails about desk setups, curated gear finds, and affiliate-supported recommendations, and occasionally other clearly identified commercial content. We will process your email address and any name you provide in accordance with our Privacy Policy. You can unsubscribe at any time.';

const config = {
  formId: MY_FORM_ID,
  template: 'clare',
  headingText: 'Subscribe to 101Setups',
  format: 'inline',
  namePlaceholder: 'Enter your name',
  emailPlaceholder: 'Enter your email address',
  disclaimerText: newsletterDisclosure,
  submitText: 'Subscribe',
};

const Footer = () => {
  const location = useLocation();
  const [analyticsConsent, setAnalyticsConsent] = useState(getAnalyticsConsent());
  const newsletterFormRef = useRef(null);
  const newsletterStartTrackedRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const syncConsent = () => {
      setAnalyticsConsent(getAnalyticsConsent());
    };

    window.addEventListener(ANALYTICS_CONSENT_UPDATED_EVENT, syncConsent);

    return () => {
      window.removeEventListener(ANALYTICS_CONSENT_UPDATED_EVENT, syncConsent);
    };
  }, []);

  useEffect(() => {
    newsletterStartTrackedRef.current = false;
  }, [location.pathname]);

  useEffect(() => {
    const formWrapper = newsletterFormRef.current;
    const form = formWrapper?.querySelector('form');

    if (!form) {
      return undefined;
    }

    const currentPageGroup = getPageGroupFromPath(location.pathname);
    const currentPagePath = location.pathname;

    const handleFormStart = event => {
      const target = event.target;
      if (newsletterStartTrackedRef.current || !(target instanceof HTMLInputElement)) {
        return;
      }

      const fieldName = String(target.name || '').toLowerCase();
      const fieldType = String(target.type || '').toLowerCase();

      if (fieldType === 'email' || fieldName.includes('email')) {
        newsletterStartTrackedRef.current = true;
        trackEventIfConsented('newsletter_form_start', {
          form_id: String(MY_FORM_ID),
          page_context: 'footer',
          page_group: currentPageGroup,
          page_path: currentPagePath,
        });
      }
    };

    const handleFormSubmit = () => {
      trackEventIfConsented('generate_lead', {
        form_id: String(MY_FORM_ID),
        lead_source: 'footer_newsletter',
        page_context: 'footer',
        page_group: currentPageGroup,
        page_path: currentPagePath,
      });
    };

    form.addEventListener('focusin', handleFormStart);
    form.addEventListener('submit', handleFormSubmit);

    return () => {
      form.removeEventListener('focusin', handleFormStart);
      form.removeEventListener('submit', handleFormSubmit);
    };
  }, [location.pathname]);

  const handleResetCookieChoice = () => {
    clearAnalyticsConsent();
    openCookieSettings();
  };

  return (
    <div className="foot">
      <div className="newsletter" id="newsletter">
        <div className="form">
          <h3>Subscribe to 101Setups</h3>
          <p>Desk setup inspiration, curated gear finds, and occasional affiliate-supported recommendations in your inbox.</p>
          <p className="newsletter-meta">
            By subscribing, you agree to receive 101Setups emails about desk setups, curated gear finds,
            affiliate-supported recommendations, and occasionally other clearly identified commercial content. We will
            process your email address and any name you provide in accordance with our <Link to="/privacy">Privacy Policy</Link>.
            You can unsubscribe at any time.
          </p>
        </div>
        <div className="form" ref={newsletterFormRef}>
          <ConvertKitForm {...config}>
            <p>Desk setup inspiration, curated gear finds, and occasional affiliate-supported recommendations.</p>
          </ConvertKitForm>
        </div>
      </div>
      <div className="footer">
        <div className="copyright">© 101 Setups 2026</div>
        <div className="footer-meta">
          <div className="footer-links">
            <Link to="/legal">Legal Notice</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/cookies">Cookie Policy</Link>
            <Link to="/terms">Terms of Use</Link>
            <Link to="/affiliate-disclosure">Affiliate Disclosure</Link>
          </div>
          <p className="footer-site-disclosure">
            <strong>{AMAZON_ASSOCIATE_LINE}</strong> {AMAZON_GLOBAL_DISCLOSURE}
          </p>
          <p className="footer-site-disclosure footer-site-disclosure-secondary">{AMAZON_STORE_DISCLOSURE}</p>
          <div className="footer-privacy-tools">
            <span className="privacy-status">Analytics: {getAnalyticsConsentStatusLabel(analyticsConsent)}</span>
            <button type="button" className="footer-action-link" onClick={openCookieSettings}>
              Cookie settings
            </button>
            <button type="button" className="footer-action-link" onClick={handleResetCookieChoice}>
              Reset choice
            </button>
          </div>
        </div>
        <div className="social">
          <nav className="followus">
            <strong>Follow Us</strong>
          </nav>
          <div className="social-links">
            <button
              type="button"
              className="social-link"
              aria-label="Follow 101Setups on Instagram"
              onClick={() => {
                trackEventIfConsented('social_click', {
                  social_network: 'instagram',
                  page_context: 'footer',
                });
                external('https://www.instagram.com/101.setups');
              }}
            >
              <Instagram className="icon" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Footer;
